// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    useMediaQuery,
    useTheme,
    FormControl,
    Autocomplete,
    TextField,
} from "@mui/material";
import {
    GridColDef,
    GridPaginationModel,
} from "@mui/x-data-grid";
import { Table, Loading, Button } from "../../../components";
import BaseFilterTableWithAccordion from "../../atoms/BaseFilterTableWithAccordion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import { useGetSalesmanMonthlyData, getSalesmanMonthlyData as getSalesmanMonthlyDataApi } from "../../../hooks";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { downloadExcel } from "../../../util/downloadExcel";
import dayjs from "dayjs";

const currentYear = new Date().getFullYear();
const startYear = 2020;
const years = [];
for (let year = currentYear; year >= startYear; year--) {
    years.push({ label: year.toString(), value: year });
}

const months = [
    { label: "Ocak", value: 1 },
    { label: "Şubat", value: 2 },
    { label: "Mart", value: 3 },
    { label: "Nisan", value: 4 },
    { label: "Mayıs", value: 5 },
    { label: "Haziran", value: 6 },
    { label: "Temmuz", value: 7 },
    { label: "Ağustos", value: 8 },
    { label: "Eylül", value: 9 },
    { label: "Ekim", value: 10 },
    { label: "Kasım", value: 11 },
    { label: "Aralık", value: 12 },
];

// Schema with optional fields
const FilterSchema = zod.object({
    year: zod.number().optional().nullable(),
    month: zod.number().optional().nullable(),
});

type FilterFormValues = zod.infer<typeof FilterSchema>;

interface SalesmanMonthlyDataTableProps {
    filters?: {
        startDate: any;
        endDate: any;
    };
}

export const SalesmanMonthlyDataTable = ({ filters: externalFilters }: SalesmanMonthlyDataTableProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const setSnackbar = useSetSnackBar();
    const [isFilterAccordionExpanded, setFilterAccordionExpand] = useState(true);
    const [showTable, setShowTable] = useState(false);
    const [isExcelLoading, setIsExcelLoading] = useState(false);

    const { control, handleSubmit, formState: { errors } } = useForm<FilterFormValues>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            year: null,
            month: null,
        }
    });

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });

    const [filters, setFilters] = useState<FilterFormValues | null>(null);

    const { mutate: getSalesmanMonthlyDataMutation, isLoading, data: listResponse } = useGetSalesmanMonthlyData();

    const [tableData, setTableData] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);

    const onSubmit = (data: FilterFormValues) => {
        setFilters(data);
        setFilterAccordionExpand(false);
        setShowTable(true);
        fetchData(data);
    };

    const fetchData = (filterData: any) => {
        const payload = {
            Page: paginationModel.page + 1,
            Size: paginationModel.pageSize,
            OrderBy: "Id",
            OrderByDesc: true
        };

        if (externalFilters) {
            payload.StartDate = externalFilters.startDate?.format("YYYY-MM-DD");
            payload.EndDate = externalFilters.endDate?.format("YYYY-MM-DD");
        } else {
            payload.Year = filterData?.year;
            payload.Month = filterData?.month;
        }

        getSalesmanMonthlyDataMutation(payload, {
            onSuccess: (response) => {
                if (response.isSuccess) {
                    const dataWithIds = (response.data?.result || []).map((row: any, index: number) => ({
                        ...row,
                        id: row.id || `${row.year}_${row.month}_${row.personnel}_${index}`
                    }));
                    setTableData(dataWithIds);
                    setTotalItems(response.data?.totalItems || 0);
                } else {
                    setTableData([]);
                    setTotalItems(0);
                    setSnackbar({
                        severity: "error",
                        description: response.message || "Veri çekilemedi",
                        isOpen: true,
                    });
                }
            },
            onError: () => {
                setTableData([]);
                setTotalItems(0);
                setSnackbar({
                    severity: "error",
                    description: "Bir hata oluştu",
                    isOpen: true,
                });
            }
        });
    };

    const handleDownloadExcel = async () => {
        const totalItemsCount = listResponse?.data?.totalItems || tableData.length;

        if (!externalFilters && !filters) return;

        setIsExcelLoading(true);
        try {
            const payload = {
                Page: 1,
                Size: totalItemsCount,
                OrderBy: "Id",
                OrderByDesc: true
            };

            if (externalFilters) {
                payload.StartDate = externalFilters.startDate?.format("YYYY-MM-DD");
                payload.EndDate = externalFilters.endDate?.format("YYYY-MM-DD");
            } else {
                payload.Year = filters?.year;
                payload.Month = filters?.month;
            }

            const response = await getSalesmanMonthlyDataApi(payload);

            if (response && response.isSuccess) {
                const mappedData = response.data?.result?.map((item: any) => ({
                "Yıl": item.year,
                "Ay": item.month,
                "Bölge": item.region,
                "Personel": item.personnel,
                "POS Ciro Hedefi": item.posTurnoverTarget,
                "POS Ciro Gerçekleşen": item.posTurnoverActual,
                "POS Ciro Oranı (%)": item.posTurnoverPercent,
                "POS Ciro Puanı": item.posTurnoverPoint,
                "Aktif POS Müşteri Hedefi": item.activePosCustomerTarget,
                "Aktif POS Müşteri Gerçekleşen": item.activePosCustomerActual,
                "Aktif POS Müşteri Oranı (%)": item.activePosCustomerPercent,
                "Aktif POS Müşteri Puanı": item.activePosCustomerPoint,
                "Yeni POS Müşteri Hedefi": item.newPosCustomerTarget,
                "Yeni POS Müşteri Gerçekleşen": item.newPosCustomerActual,
                "Yeni POS Müşteri Oranı (%)": item.newPosCustomerPercent,
                "Yeni POS Müşteri Puanı": item.newPosCustomerPoint,
                "Yeni Kart Satış Hedefi": item.newCardSalesTarget,
                "Yeni Kart Satış Gerçekleşen": item.newCardSalesActual,
                "Yeni Kart Satış Oranı (%)": item.newCardSalesPercent,
                "Yeni Kart Satış Puanı": item.newCardSalesPoint,
                "Yeni Cüzdan Hedefi": item.newWalletTarget,
                "Yeni Cüzdan Gerçekleşen": item.newWalletActual,
                "Yeni Cüzdan Oranı (%)": item.newWalletPercent,
                "Yeni Cüzdan Puanı": item.newWalletPoint,
                "Toplam Puan": item.totalPoint,
                "Karlılık Hedefi": item.profitGoal,
                "Karlılık": item.profit
                })) || [];

                downloadExcel(mappedData, "Aylık Satış Performans Raporu");
                }
                } catch (error) {
                setSnackbar({ severity: "error", description: "Excel indirme hatası", isOpen: true });
                } finally {
                setIsExcelLoading(false);
                }
                };

                useEffect(() => {
                if (externalFilters) {
                fetchData();
                setShowTable(true);
                } else if (filters) {
                fetchData(filters);
                } else {
                const initialFilters = { year: null, month: null };
                setFilters(initialFilters);
                fetchData(initialFilters);
                setShowTable(true);
                }
                }, [paginationModel, externalFilters?.startDate, externalFilters?.endDate]);

                // Columns
                const columns: GridColDef[] = useMemo(() => [
                { field: "year", headerName: "Yıl", width: 80 },
                { field: "month", headerName: "Ay", width: 80 },
                { field: "region", headerName: "Bölge", width: 150 },
                { field: "personnel", headerName: "Personel", width: 200 },
                { field: "posTurnoverTarget", headerName: "POS Ciro Hedefi", width: 150, type: 'decimal' },
                { field: "posTurnoverActual", headerName: "POS Ciro Gerçekleşen", width: 160, type: 'decimal' },
                { field: "posTurnoverPercent", headerName: "POS Ciro Oranı (%)", width: 150, type: 'decimalPercentage' },
                { field: "posTurnoverPoint", headerName: "POS Ciro Puanı", width: 130, type: 'decimalOnly' },
                { field: "profitGoal", headerName: "Karlılık Hedefi", width: 150, type: 'decimal' },
                { field: "profit", headerName: "Karlılık", width: 150, type: 'decimal' },
                { field: "activePosCustomerTarget", headerName: "Aktif POS Müşteri Hedefi", width: 180, type: 'decimalOnly' },
                { field: "activePosCustomerActual", headerName: "Aktif POS Müşteri Gerçek.", width: 180, type: 'decimalOnly' },
                { field: "activePosCustomerPercent", headerName: "Aktif POS Müşteri Oranı (%)", width: 200, type: 'decimalPercentage' },
                { field: "activePosCustomerPoint", headerName: "Aktif POS Müşteri Puanı", width: 180, type: 'decimalOnly' },
                { field: "newPosCustomerTarget", headerName: "Yeni POS Müşteri Hedefi", width: 180, type: 'decimalOnly' },
                { field: "newPosCustomerActual", headerName: "Yeni POS Müşteri Gerçekleşen", width: 200, type: 'decimalOnly' },
                { field: "newPosCustomerPercent", headerName: "Yeni POS Müşteri Oranı (%)", width: 190, type: 'decimalPercentage' },
                { field: "newPosCustomerPoint", headerName: "Yeni POS Müşteri Puanı", width: 180, type: 'decimalOnly' },
                { field: "newCardSalesTarget", headerName: "Yeni Kart Satış Hedefi", width: 170, type: 'decimalOnly' },
                { field: "newCardSalesActual", headerName: "Yeni Kart Satış Gerçekleşen", width: 190, type: 'decimalOnly' },
                { field: "newCardSalesPercent", headerName: "Yeni Kart Satış Oranı (%)", width: 180, type: 'decimalPercentage' },
                { field: "newCardSalesPoint", headerName: "Yeni Kart Satış Puanı", width: 160, type: 'decimalOnly' },
                { field: "newWalletTarget", headerName: "Yeni Cüzdan Hedefi", width: 160, type: 'decimalOnly' },
                { field: "newWalletActual", headerName: "Yeni Cüzdan Gerçekleşen", width: 180, type: 'decimalOnly' },
                { field: "newWalletPercent", headerName: "Yeni Cüzdan Oranı (%)", width: 170, type: 'decimalPercentage' },
                { field: "newWalletPoint", headerName: "Yeni Cüzdan Puanı", width: 150, type: 'decimalOnly' },
                { field: "totalPoint", headerName: "Toplam Puan", width: 120, type: 'decimalOnly' },
                ], []);
    return (
        <Box sx={{ p: 2 }}>
            {(isLoading || isExcelLoading) && <Loading />}
            {!externalFilters && (
                <BaseFilterTableWithAccordion
                    isExpanded={isFilterAccordionExpanded}
                    setExpanded={setFilterAccordionExpand}
                    setTableVisibility={setShowTable}
                    summary="Filtreleme"
                    child={
                        <Stack spacing={2} direction={isDesktop ? "row" : "column"}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <Controller
                                    control={control}
                                    name="year"
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={years}
                                            getOptionLabel={(option) => option.label}
                                            value={years.find(y => y.value === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue?.value || null)}
                                            renderInput={(params) => <TextField {...params} label="Yıl" />}
                                        />
                                    )}
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200 }}>
                                <Controller
                                    control={control}
                                    name="month"
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={months}
                                            getOptionLabel={(option) => option.label}
                                            value={months.find(m => m.value === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue?.value || null)}
                                            renderInput={(params) => <TextField {...params} label="Ay" />}
                                        />
                                    )}
                                />
                            </FormControl>
                            <Button text="LİSTELE" variant="contained" onClick={handleSubmit(onSubmit)} />
                        </Stack>
                    }
                />
            )}

            <Box sx={{ mt: externalFilters ? 0 : 2 }}>
                {showTable && (
                    <Table
                        rows={tableData}
                        columns={columns}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100, 250]}
                        paginationMode="server"
                        onSave={handleDownloadExcel}
                        rowCount={totalItems}
                        getRowId={(row) => row.id}
                        autoHeight={tableData.length > 0}
                        sx={{ minHeight: '60vh' }}
                    />
                )}
            </Box>
        </Box>
    );
};
